/* eslint-disable react/jsx-no-useless-fragment */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { omit } from 'lodash';
import type {
  AutosuggestPropsMultiSection,
  AutosuggestPropsSingleSection,
  ChangeEvent,
  InputProps as AutosuggestInputProps,
  OnSuggestionHighlighted,
  RenderInputComponent,
  RenderSuggestionsContainer,
  SuggestionsFetchRequested,
} from 'react-autosuggest';
import Autosuggest from 'react-autosuggest';
import { useOnPropChanged } from '$util/hooks/useOnPropChanged';
import { InjectComponent } from '$util/InjectComponent';
import { SuggestionsContainer } from './components/SuggestionsContainer';
import type { SearchInputProps } from './components/SearchInput';
import { SearchInput } from './components/SearchInput';

export enum AutocompleteInjectName {
  SUGGESTION = 'suggestion',
  SUGGESTIONS_CONTAINER = 'suggestions-container',
  SUGGESTION_MATCH = 'suggestion-match',
}

export interface AutocompleteInjectParams<TSuggestion> {
  [AutocompleteInjectName.SUGGESTION]: {
    readonly value: string;
    readonly suggestion: TSuggestion;
  };
}

type PickAutosuggestProps<TSuggestion> = Pick<
  AutosuggestPropsSingleSection<TSuggestion>,
  | 'alwaysRenderSuggestions'
  | 'getSuggestionValue'
  | 'onSuggestionSelected'
  | 'onSuggestionHighlighted'
  | 'shouldRenderSuggestions'
>;

type PickAutosuggestInputProps<TSuggestion> = Partial<
  Pick<AutosuggestInputProps<TSuggestion>, 'placeholder' | 'onFocus' | 'onBlur'>
>;

type PickSearchInputProps = Pick<
  SearchInputProps,
  | 'autoFocus'
  | 'addonAfter'
  | 'addonBefore'
  | 'prefix'
  | 'suffix'
  | 'allowClear'
  | 'onSearch'
  | 'onKeyUp'
  | 'spellCheck'
>;

type BaseProps<TSuggestion> = PickAutosuggestProps<TSuggestion> &
  PickAutosuggestInputProps<TSuggestion> &
  PickSearchInputProps;

export interface AutocompleteProps<TSuggestion, TSection = unknown> extends BaseProps<TSuggestion> {
  readonly id: string;
  readonly className?: string;
  readonly isLoadingSuggestions?: boolean;
  readonly getSuggestions: (value: string) => TSuggestion[];
  readonly getSuggestionValue: (suggestion: TSuggestion) => string;
  readonly initialValue?: string;
  /**
   * Changes the internal state for the input value.
   * Try to avoid using it, for most of the cases returning a value from `onChange` should be enough.
   * To go back to using the internal state set this to `undefined`.
   */
  readonly inputValue?: string;
  readonly multiSection?: Required<
    Pick<AutosuggestPropsMultiSection<TSuggestion, TSection>, 'renderSectionTitle' | 'getSectionSuggestions'>
  >;
  /**
   * If it returns a string it will be the new value, otherwise it will use the `ChangeEvent.newValue`
   */
  readonly onChange?: (event: React.FormEvent<HTMLElement>, params: ChangeEvent) => void | string;
  readonly onClear?: () => void;
  readonly useDefaultNoSuggestions?: boolean;
}

interface AutocompleteState<TSuggestion> {
  readonly value: string;
  readonly suggestions: TSuggestion[];
  readonly inputInFocus: boolean;
  readonly enterPressed: boolean;
}

export function Autocomplete<TSuggestion, TSection = unknown>({
  id,
  className,
  isLoadingSuggestions = false,
  getSuggestions,
  initialValue = '',
  inputValue,
  onClear,
  useDefaultNoSuggestions = true,
  /**
   * Autosuggest props
   */
  alwaysRenderSuggestions = false,
  getSuggestionValue,
  onSuggestionSelected,
  onSuggestionHighlighted,
  shouldRenderSuggestions,
  multiSection,
  /**
   * AutosuggestInputProps
   */
  placeholder,
  onChange,
  onFocus,
  onBlur,
  /**
   * SearchInput props
   */
  onSearch,
  onKeyUp,
  autoFocus = false,
  addonAfter,
  addonBefore,
  prefix,
  suffix,
  allowClear = true,
  spellCheck,
}: AutocompleteProps<TSuggestion, TSection>): JSX.Element {
  type State = AutocompleteState<TSuggestion>;
  type InputProps = AutosuggestInputProps<TSuggestion>;

  const [{ value, suggestions, inputInFocus, enterPressed }, setState] = useState<State>({
    value: initialValue,
    suggestions: alwaysRenderSuggestions ? getSuggestions(initialValue) : [],
    inputInFocus: false,
    enterPressed: false,
  });

  /**
   * Set input value from outside.
   * Try to avoid using it because it adds additional renders,
   * if we use it often we should refactor to make the internal state available from outside.
   */
  useEffect(() => {
    if (typeof inputValue !== 'undefined') {
      setState((prev) => ({
        ...prev,
        value: inputValue,
      }));
    }
  }, [inputValue]);

  /**
   * Autosuggest by default closes suggestions container at Enter key press.
   * We'll keep it open by forcing `alwaysRenderSuggestions`.
   * @see https://github.com/moroshko/react-autosuggest/blob/a1ebd7d29aa60a355898664c45916d581abd1b4e/src/Autosuggest.js#L721-L723
   */
  const alwaysDisplaySuggestions = alwaysRenderSuggestions || enterPressed;
  const displaySuggestions = alwaysDisplaySuggestions || inputInFocus;

  const inputProps = useMemo<InputProps>(
    () => ({
      value,
      placeholder,
      spellCheck,
      onChange: (e, change) => {
        let transformedValue = onChange?.(e, change);
        if (typeof transformedValue === 'undefined') transformedValue = change.newValue;
        if (transformedValue === '' && !(e.nativeEvent instanceof InputEvent)) {
          // deduce onClear event because antd doesn't provide one
          onClear?.();
        }
        const newValue = transformedValue;
        setState((prevState) => ({
          ...prevState,
          value: newValue,
          enterPressed: false,
        }));
      },
      onFocus: (e) => {
        onFocus?.(e);
        setState((prevState) => ({
          ...prevState,
          inputInFocus: true,
          enterPressed: false,
        }));
      },
      onBlur: (e) => {
        onBlur?.(e);
        setState((prevState) => ({
          ...prevState,
          inputInFocus: false,
          enterPressed: false,
        }));
      },
    }),
    [onBlur, onChange, onClear, onFocus, placeholder, spellCheck, value]
  );

  const multiSectionProps = useMemo(() => {
    if (!multiSection) return {};
    return { multiSection: true, ...multiSection };
  }, [multiSection]);

  const onSuggestionsFetchRequested: SuggestionsFetchRequested = useCallback(
    (request) => {
      const results = getSuggestions(request.value);
      setState((prevState) => ({ ...prevState, suggestions: results }));
    },
    [getSuggestions]
  );

  const onSuggestionsClearRequested = () => {
    if (!displaySuggestions) {
      setState((prevState) => ({ ...prevState, suggestions: [] }));
    }
  };

  const handleSuggestionHighlighted: OnSuggestionHighlighted = (params) => {
    onSuggestionHighlighted?.(params);
  };

  useOnPropChanged(getSuggestions, () => {
    /**
     * Set suggestions when loading has finished.
     */
    if (!isLoadingSuggestions) onSuggestionsFetchRequested({ value, reason: 'suggestions-revealed' });
  });

  const renderInputComponent: RenderInputComponent = (searchInputProps) => (
    <SearchInput
      addonAfter={addonAfter}
      addonBefore={addonBefore}
      prefix={prefix}
      suffix={suffix}
      allowClear={allowClear}
      autoFocus={autoFocus}
      onSearch={(v, e) => {
        onSearch?.(v, e);
        const enterKey = !!e && 'keyCode' in e && e.keyCode === 13;
        setState((prevState) => ({ ...prevState, enterPressed: enterKey }));
      }}
      onKeyUp={(e) => {
        onKeyUp?.(e);
        searchInputProps.onKeyUp?.(e);
      }}
      {...omit(searchInputProps, ['size', 'onKeyUp'])}
    />
  );

  const renderSuggestionsContainer: RenderSuggestionsContainer = ({
    containerProps: suggestionsContainerProps,
    children,
  }) =>
    displaySuggestions && (
      <SuggestionsContainer
        value={value}
        isLoading={isLoadingSuggestions}
        isEmpty={useDefaultNoSuggestions && !suggestions?.length && !!value?.length}
        {...suggestionsContainerProps}
      >
        <InjectComponent
          name={AutocompleteInjectName.SUGGESTIONS_CONTAINER}
          namespace={Autocomplete.displayName}
        >
          <>{children}</>
        </InjectComponent>
      </SuggestionsContainer>
    );

  const renderSuggestion = (suggestion: TSuggestion) => (
    <InjectComponent<AutocompleteInjectParams<TSuggestion>[AutocompleteInjectName.SUGGESTION]>
      name={AutocompleteInjectName.SUGGESTION}
      namespace={Autocomplete.displayName}
      params={{ value, suggestion }}
    >
      <div className="suggestion">{getSuggestionValue(suggestion)}</div>
    </InjectComponent>
  );

  return (
    <div className={classNames('Autocomplete', className)}>
      <Autosuggest
        id={id}
        inputProps={inputProps}
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        renderSuggestionsContainer={renderSuggestionsContainer}
        renderSuggestion={renderSuggestion}
        renderInputComponent={renderInputComponent}
        onSuggestionSelected={onSuggestionSelected}
        onSuggestionHighlighted={handleSuggestionHighlighted}
        alwaysRenderSuggestions={alwaysDisplaySuggestions}
        shouldRenderSuggestions={shouldRenderSuggestions}
        highlightFirstSuggestion={enterPressed}
        focusInputOnSuggestionClick={false}
        {...multiSectionProps}
      />
    </div>
  );
}
Autocomplete.displayName = 'Autocomplete';
